import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Layers, Wrench, ClipboardCheck, ArrowRight, Clock3, Building2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { getMyAssignments } from "../lib/clientAccess";

const MODULE_ICON = {
  smartevaluate: Layers,
  smartimprove: Wrench,
  smartinspect: ClipboardCheck,
};

/**
 * My Evaluations — client-facing list of finalized evaluations shared with the
 * signed-in user. Content-only: it renders INSIDE the shared application shell
 * (ConsultantLayout) so the client sees the same layout + sidebar as staff.
 */
export default function MyEvaluationsPage() {
  const navigate = useNavigate();
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setEvaluations(await getMyAssignments());
    } catch {
      toast.error("Failed to load your evaluations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = (a) => {
    if (a?.view_route) navigate(a.view_route);
    else toast.error("Unable to open this evaluation");
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="my-evaluations-page">
      <div className="mb-8">
        <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#025979]">
          PrimeS4 AI
        </p>
        <h1
          className="mt-1 text-3xl font-bold text-[#0F172A] tracking-tight"
          style={{ fontFamily: "Chivo, sans-serif" }}
          data-testid="my-evaluations-title"
        >
          My Evaluations
        </h1>
        <p className="mt-2 text-sm text-[#64748B] max-w-2xl">
          The finalized evaluations shared with you. Open any evaluation to review
          the complete findings, recommendations, roadmap, ROI and reports.
        </p>
      </div>

      {loading ? (
        <div className="py-20 text-center text-sm text-[#64748B]" data-testid="my-evaluations-loading">
          Loading your evaluations…
        </div>
      ) : evaluations.length === 0 ? (
        <div
          className="bg-white border border-[#E5E7EB] rounded-sm py-16 px-8 text-center max-w-xl mx-auto"
          data-testid="my-evaluations-empty"
        >
          <div className="mx-auto w-12 h-12 rounded-sm flex items-center justify-center bg-[#E0F2FE] text-[#025979]">
            <Clock3 className="w-6 h-6" />
          </div>
          <h2 className="mt-5 text-xl font-bold text-[#0F172A]" style={{ fontFamily: "Chivo, sans-serif" }}>
            No evaluations shared yet
          </h2>
          <p className="mt-2 text-sm text-[#64748B]">
            Your consultant has not shared any finalized evaluations with this account.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] rounded-sm overflow-hidden" data-testid="my-evaluations-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F8FAFC] border-b border-[#E5E7EB]">
                <th className="text-left px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#64748B]">Module</th>
                <th className="text-left px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#64748B]">Evaluation</th>
                <th className="text-right px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#64748B]">Action</th>
              </tr>
            </thead>
            <tbody>
              {evaluations.map((a) => {
                const Icon = MODULE_ICON[a.module] || Building2;
                return (
                  <tr
                    key={a.grant_id}
                    className="border-b border-[#F1F5F9] last:border-0 hover:bg-[#F8FAFC]/60"
                    data-testid={`evaluation-row-${a.assignment_id}`}
                  >
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center gap-2 text-[#025979] font-semibold">
                        <span className="w-7 h-7 rounded-sm bg-[#E0F2FE] flex items-center justify-center">
                          <Icon className="w-3.5 h-3.5" />
                        </span>
                        {a.module_label}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <p className="font-semibold text-[#0F172A]">{a.title}</p>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <Button
                        onClick={() => open(a)}
                        className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm h-8 px-3.5 text-xs font-semibold"
                        data-testid={`evaluation-view-${a.assignment_id}`}
                      >
                        View Evaluation <ArrowRight className="w-3.5 h-3.5 ml-1" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
